<template>
  <div class="login-container">
    <div class="login-side">
      <v-card
        width="400"
        class="mx-auto mt-5 login-form">
        <v-card-title id="login-title">HSP Brands Client Dashboard</v-card-title>

        <div
          v-if="error"
          class="alert"
          role="alert">
          {{ error }}
        </div>
        <div
          v-if="notice"
          class="notice"
          role="status">
          {{ notice }}
        </div>

        <v-card-text v-if="!showPasswordUpdateForm && !showForgotPasswordForm">
          <v-form
            id="loginForm"
            ref="loginForm"
            @submit.prevent="handleLogin">
            <v-text-field
              v-model="username"
              label="Username"
              autocomplete="username"
              required
            />
            <v-text-field
              v-model="password"
              label="Password"
              type="password"
              autocomplete="current-password"
              required
            />
            <v-btn
              :loading="loading"
              type="submit"
              color="primary"
              block
              class="mt-2">
              Login
            </v-btn>
          </v-form>
          <v-btn
            flat
            small
            color="primary"
            class="forgot-password-link"
            @click="openForgotPassword">
            Forgot password?
          </v-btn>
          <v-divider />
          <div>
            Please email <a href="mailto:support@hspbrands.com">support@hspbrands.com</a> for help signing in.
          </div>
        </v-card-text>

        <v-card-text v-if="showForgotPasswordForm">
          <v-card-title class="px-0">Reset Password</v-card-title>
          <p>Enter the real email address associated with your account.</p>
          <v-form @submit.prevent="requestPasswordReset">
            <v-text-field
              v-model="resetEmail"
              label="Email"
              type="email"
              autocomplete="email"
              required
            />
            <v-btn
              :loading="loading"
              type="submit"
              color="primary"
              block>
              Send Reset Instructions
            </v-btn>
            <v-btn
              flat
              block
              class="mt-2"
              @click="closeForgotPassword">Back to Login</v-btn>
          </v-form>
        </v-card-text>

        <v-card-text v-if="showPasswordUpdateForm">
          <v-card-title class="px-0">Create New Password</v-card-title>
          <v-form @submit.prevent="updatePassword">
            <v-text-field
              v-model="newPassword"
              :rules="[v => !!v || 'Password is required', passwordStrengthRule]"
              label="New Password"
              type="password"
              autocomplete="new-password"
              required
            />
            <v-text-field
              v-model="confirmPassword"
              :rules="[v => v === newPassword || 'Passwords do not match']"
              label="Confirm Password"
              type="password"
              autocomplete="new-password"
              required
            />
            <v-btn
              :disabled="!isPasswordValid"
              :loading="loading"
              type="submit"
              color="primary"
              block>
              Update Password
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </div>

    <div class="brand-side">
      <div class="brand-content">
        <img
          :src="require('@/assets/hsp-pour.png')"
          alt="Product sampling"
          class="brand-photo">
      </div>
    </div>
  </div>
</template>

<script>
import { TokenService } from '../services/storage.service'

export default {
  data () {
    return {
      username: '',
      password: '',
      error: null,
      notice: null,
      loading: false,
      showForgotPasswordForm: false,
      showPasswordUpdateForm: false,
      resetEmail: '',
      newPassword: '',
      confirmPassword: '',
      pendingLoginResponse: null
    }
  },

  computed: {
    isPasswordValid () {
      return this.newPassword &&
        this.confirmPassword &&
        this.newPassword === this.confirmPassword &&
        this.newPassword.length >= 12
    }
  },

  created () {
    this.username = window.__TASTINGIQ_LOGIN_USERNAME__ ||
      window.sessionStorage.getItem('tastingiq_login_username') || ''
    delete window.__TASTINGIQ_LOGIN_USERNAME__
    window.sessionStorage.removeItem('tastingiq_login_username')
  },

  methods: {
    passwordStrengthRule (password) {
      return password.length >= 12 || 'Password must be at least 12 characters'
    },

    responseMessage (error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : ((error && error.message) || fallback)
    },

    saveLoginResponse (response) {
      this.$store.commit('setLoginCredentials', {
        code: response.code,
        accessKey: response.access_key,
        type: response.type,
        username: response.username,
        user: response.user || null
      })
      TokenService.saveUsername(response.username)
    },

    safeDestination (path) {
      return typeof path === 'string' && path.charAt(0) === '/' && path.substring(0, 2) !== '//'
        ? path
        : null
    },

    destinationFor (response) {
      const serverDestination = this.safeDestination(response.return_to)
      const requestedDestination = this.safeDestination(this.$route.query.redirect || this.$route.query.return)
      if (serverDestination) return serverDestination
      if (requestedDestination) return requestedDestination
      return response.type === 'retailer' || response.type === 'retail' ? '/calendar' : '/analytics'
    },

    async establishSession (response) {
      try {
        if (response.type === 'client') {
          await this.$store.dispatch('set_client', {
            access_key: response.access_key,
            client_code: response.code
          })
        } else if (response.type === 'retailer' || response.type === 'retail') {
          await this.$store.dispatch('set_retail', {
            retail_key: response.access_key,
            retail: response.code
          })
        } else {
          throw new Error('Unknown account type')
        }

        TokenService.setSeenLoginPage()
        await this.$router.replace(this.destinationFor(response))
      } catch (error) {
        TokenService.clearLogin()
        this.$store.commit('setUserProfile', null)
        throw error
      }
    },

    async handleLogin () {
      this.error = null
      this.notice = null
      this.loading = true
      TokenService.clearLogin()
      this.$store.commit('setUserProfile', null)

      try {
        const response = await this.$store.dispatch('login', {
          username: this.username,
          password: this.password
        })

        this.saveLoginResponse(response)
        this.pendingLoginResponse = response

        if (response.status === 'success') {
          await this.establishSession(response)
        } else if (response.status === 'updatePassword') {
          this.showPasswordUpdateForm = true
        } else {
          this.error = response.message || 'Login failed. Please check your credentials.'
        }
      } catch (error) {
        this.error = this.responseMessage(error, 'Login failed. Please check your credentials.')
      } finally {
        this.loading = false
      }
    },

    openForgotPassword () {
      this.error = null
      this.notice = null
      this.showForgotPasswordForm = true
    },

    closeForgotPassword () {
      this.showForgotPasswordForm = false
      this.error = null
    },

    async requestPasswordReset () {
      this.error = null
      this.notice = null
      this.loading = true
      try {
        const response = await this.$store.dispatch('forgotPassword', { email: this.resetEmail })
        this.notice = response.message
      } catch (error) {
        this.error = this.responseMessage(error, 'Unable to request a password reset. Please try again.')
      } finally {
        this.loading = false
      }
    },

    async updatePassword () {
      if (!this.isPasswordValid || !this.pendingLoginResponse) return

      this.error = null
      this.loading = true
      try {
        const response = await this.$store.dispatch('updatePassword', {
          username: this.username,
          newPassword: this.newPassword,
          oldPassword: this.password
        })

        if (response.status === 'success') {
          await this.establishSession(this.pendingLoginResponse)
        } else {
          this.error = response.message || 'Password update failed. Please try again.'
        }
      } catch (error) {
        this.error = this.responseMessage(error, 'An error occurred while updating the password. Please try again.')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.alert,
.notice {
  margin: 0 16px;
  padding: 10px 12px;
  border-radius: 4px;
}

.alert {
  color: #b71c1c;
  background: #ffebee;
}

.notice {
  color: #1b5e20;
  background: #e8f5e9;
}

.forgot-password-link {
  margin: 8px 0;
  text-transform: none;
}
</style>
